// ModelPicker — shared radio group for the three picker locations.
//
// mode="site"     → writes to website_ai_preferences (uses ModelPicker's onChange)
// mode="org"      → writes to company_ai_preferences (caller supplies onChange)
// mode="request"  → ephemeral; choice only persisted if user opts in upstream
//
// The picker fetches list-available-models + list-locked-models and renders
// every entry. Cards the org tier can't access are visibly locked with an
// upgrade CTA. Below the primary picker is an optional fallback dropdown,
// limited to models cheaper or equal to the primary.
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ModelCard } from "./ModelCard";
import {
  listAvailableModels,
  listLockedModels,
  getWallet,
  type ModelEntry,
  type TaskType,
} from "@/lib/credits/api";

type Mode = "site" | "org" | "request";

type Props = {
  orgId: string;
  siteId?: string | null;
  taskType: TaskType;
  primary: string | null;
  fallback?: string | null;
  onPrimaryChange: (modelKey: string | null) => void;
  onFallbackChange?: (modelKey: string | null) => void;
  mode: Mode;
  showFallback?: boolean;
  showBalance?: boolean;
};

export function ModelPicker({
  orgId,
  siteId,
  taskType,
  primary,
  fallback,
  onPrimaryChange,
  onFallbackChange,
  mode,
  showFallback = true,
  showBalance = true,
}: Props) {
  const { t } = useTranslation();

  const available = useQuery({
    queryKey: ["available-models", orgId, taskType, siteId ?? null],
    queryFn: () => listAvailableModels(orgId, taskType, siteId ?? null),
    enabled: !!orgId,
  });
  const locked = useQuery({
    queryKey: ["locked-models", orgId, taskType],
    queryFn: () => listLockedModels(orgId, taskType),
    enabled: !!orgId,
  });
  const wallet = useQuery({
    queryKey: ["wallet", orgId],
    queryFn: () => getWallet(orgId),
    enabled: !!orgId && showBalance,
  });

  const allModels: ModelEntry[] = useMemo(() => {
    const a = available.data?.models ?? [];
    const l = locked.data?.models ?? [];
    return [...a, ...l];
  }, [available.data, locked.data]);

  const defaultKey = available.data?.default_model_key ?? null;

  // Fallback options: only accessible (unlocked) models with cost <= primary cost.
  const primaryCost = useMemo(() => {
    const p = allModels.find((m) => m.key === primary);
    return p?.credits_cost ?? 0;
  }, [allModels, primary]);

  const fallbackOptions = useMemo(() => {
    return (available.data?.models ?? []).filter((m) => m.key !== primary && m.credits_cost <= primaryCost);
  }, [available.data, primary, primaryCost]);

  if (available.isLoading || locked.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t("featureGate.loading")}
      </div>
    );
  }

  if (allModels.length === 0) {
    return <p className="text-sm text-muted-foreground py-4">{t("models.noModelsAvailable")}</p>;
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup">
        {allModels.map((m) => (
          <ModelCard
            key={m.key}
            model={m}
            selected={!m.locked && m.key === primary}
            isDefault={m.key === defaultKey}
            balance={wallet.data?.balance ?? null}
            onClick={() => onPrimaryChange(m.key)}
          />
        ))}
      </div>

      {showFallback && onFallbackChange && fallbackOptions.length > 0 && (
        <div className="border-t pt-3">
          <Label className="text-xs font-medium mb-1.5 block">
            {t("models.fallbackLabel")}
          </Label>
          <p className="text-xs text-muted-foreground mb-2">{t("models.fallbackHelp")}</p>
          <Select
            value={fallback ?? "__none"}
            onValueChange={(v) => onFallbackChange(v === "__none" ? null : v)}
          >
            <SelectTrigger className="w-full sm:w-72">
              <SelectValue placeholder={t("models.fallbackNone")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">{t("models.fallbackNone")}</SelectItem>
              {fallbackOptions.map((m) => (
                <SelectItem key={m.key} value={m.key}>
                  {m.label} · {m.credits_cost} cr
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {mode === "site" && primary && (
        <button
          type="button"
          className="text-xs text-muted-foreground hover:text-foreground hover:underline"
          onClick={() => onPrimaryChange(null)}
        >
          {t("models.resetToOrg")}
        </button>
      )}
    </div>
  );
}

export default ModelPicker;
